import {
  attempt,
  buildStartupReport,
  buildStepLine,
  createStartupHook,
  startupRequests,
  startupTrace,
  writeDiagnosticsOnce,
  type RequestTracker,
  type StartupTrace,
} from '@/startup/startup-diagnostics';

export const STARTUP_TIMEOUT_MS = 15_000;

const UNKNOWN_STEP = 'Start';

export class StartupError extends Error {
  readonly step: string;
  readonly cause: unknown;

  constructor(step: string, cause: unknown) {
    super(`Startschritt "${step}" fehlgeschlagen: ${describeCause(cause)}`);
    this.name = 'StartupError';
    this.step = step;
    this.cause = cause;
  }
}

function describeCause(cause: unknown): string {
  if (cause instanceof Error) return cause.message || cause.name;
  return String(cause);
}

function isHttpClientError(
  cause: unknown
): cause is { config?: { method?: string; url?: string }; response?: { status?: number } } {
  return (
    typeof cause === 'object' && cause !== null && ('config' in cause || 'response' in cause || 'isAxiosError' in cause)
  );
}

function describeHttpCause(cause: {
  config?: { method?: string; url?: string };
  response?: { status?: number };
}): string {
  const parts: string[] = [];
  if (cause.response?.status !== undefined) parts.push(`HTTP ${cause.response.status}`);
  if (cause.config?.method) parts.push(cause.config.method.toUpperCase());
  if (cause.config?.url) parts.push(cause.config.url.split('?')[0]);
  return parts.length ? `${describeCause(cause)} [${parts.join(' ')}]` : describeCause(cause);
}

export async function runStartupStep<T>(step: string, action: () => Promise<T> | T): Promise<T> {
  startupTrace.beginStep(step);
  try {
    const result = await action();
    startupTrace.endStep(step, 'abgeschlossen');
    return result;
  } catch (error) {
    startupTrace.endStep(step, 'fehlgeschlagen');
    throw error instanceof StartupError ? error : new StartupError(step, error);
  }
}

function timeoutError(step: string): StartupError {
  return new StartupError(step, new Error(`Zeitüberschreitung (Startbudget von ${STARTUP_TIMEOUT_MS / 1000} s)`));
}

export function withStartupTimeout<T>(
  promise: Promise<T>,
  step: string,
  startedAt: number,
  now: () => number = Date.now
): Promise<T> {
  const remainingMs = Math.max(0, STARTUP_TIMEOUT_MS - (now() - startedAt));

  if (remainingMs === 0) {
    promise.catch(() => undefined);
    return Promise.reject(timeoutError(step));
  }

  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(timeoutError(step)), remainingMs);

    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error instanceof StartupError ? error : new StartupError(step, error));
      }
    );
  });
}

function logStartupFailure(...details: unknown[]): void {
  // eslint-disable-next-line no-console
  console.error(...details);
}

function writeDiagnostics(doc: Document, trace: StartupTrace, requests: RequestTracker, idpUrl?: string): void {
  attempt(() => {
    const line = doc.querySelector('[data-testid="startup-error-step"]');
    if (line) line.textContent = buildStepLine(trace.lastStep(), trace.now());
  });
  attempt(() =>
    writeDiagnosticsOnce(trace, () => buildStartupReport({ trace, requests, doc, idpUrl }), logStartupFailure)
  );
}

export function registerStartupHook(win: Window = window, idpUrl?: string): void {
  attempt(() => {
    win.__startupDiagnostics = createStartupHook({
      trace: startupTrace,
      requests: startupRequests,
      write: logStartupFailure,
      doc: win.document,
      idpUrl,
    });
  });
}

export function reportStartupFailure(
  error: unknown,
  doc: Document = document,
  trace: StartupTrace = startupTrace,
  requests: RequestTracker = startupRequests,
  idpUrl?: string
): void {
  const step = error instanceof StartupError ? error.step : UNKNOWN_STEP;
  const cause = error instanceof StartupError ? error.cause : error;

  if (!doc.querySelector('[data-testid="startup-error"]')?.hasAttribute('data-start-logged')) {
    if (isHttpClientError(cause)) {
      logStartupFailure(`[Start] Schritt "${step}" fehlgeschlagen: ${describeHttpCause(cause)}`);
    } else {
      logStartupFailure(`[Start] Schritt "${step}" fehlgeschlagen:`, cause);
    }
  }

  doc.querySelector('[data-testid="startup-loader"]')?.setAttribute('hidden', '');

  const detail = doc.querySelector('[data-testid="startup-error-detail"]');
  if (detail) detail.textContent = `Schritt: ${step} – ${describeCause(cause)}`;

  doc.querySelector('[data-testid="startup-error"]')?.removeAttribute('hidden');

  writeDiagnostics(doc, trace, requests, idpUrl);
}
